import { useState, useRef } from 'react';
import type {
  AnalysisOption,
  EventActivationMeta,
  EventAudience,
  EventSupport,
} from '../../shared/types';
import {
  EVENT_AUDIENCE_OPTIONS,
  EVENT_SUPPORT_OPTIONS,
} from '../../shared/types';
import AnalysisIcon from './ui/AnalysisIcons';
import { useAuth } from '../contexts/AuthContext';
import TrademarkAvailabilitySection from './TrademarkAvailabilitySection';

interface Props {
  option: AnalysisOption;
  error: string | null;
  onSubmit: (data: {
    textContent?: string;
    context?: string;
    images?: File[];
    event?: EventActivationMeta;
  }) => void;
  onBack: () => void;
}

// Support-specific placeholders — prompt the user for the facts that
// actually flip the verdict (pourquoi, comment, combien, dans quel but).
function eventSupportPlaceholder(support: EventSupport): string {
  switch (support) {
    case 'goodies':
      return 'Ex: 500 tire-bouchons marqués, distribués gratuitement aux visiteurs à l\'entrée du salon. But : faire connaître la marque auprès des cavistes présents.';
    case 'plv':
      return 'Ex: stop-rayon A3 posé devant les bouteilles dans le rayon vins d\'une enseigne, indiquant AOP + médaille. Durée : 3 semaines.';
    case 'stand':
      return 'Ex: stand 12 m² au salon Wine Paris, scénographie en bois, grand visuel bouteille 2m, 2 mange-debout pour dégustation.';
    case 'affichage_local':
      return 'Ex: 20 affichettes A3 disposées dans le caveau et sur le parcours de visite. Mention sanitaire en bas. But : orienter et informer les visiteurs.';
    case 'signaletique':
      return 'Ex: 3 oriflammes aux couleurs du domaine devant le chai, visibles depuis la route départementale. But : signaler l\'opération portes ouvertes.';
    case 'degustation':
      return 'Ex: dégustation gratuite de 3 cuvées, verres de 3cl, pupitre de dégustation, 1 hôtesse formée. Vérification d\'âge à l\'entrée. But : faire découvrir la gamme.';
    case 'personnel':
      return 'Ex: 2 hôtesses en polo aux couleurs du domaine. Script : présenter l\'origine, le cépage, le mode d\'élaboration. Pas de sollicitation active des passants.';
    case 'menu_carte':
      return 'Ex: mise en avant de notre cuvée en tête de carte du bar partenaire, avec pairing suggéré. Durée : 2 mois.';
    case 'autre':
      return 'Décrivez librement le support, son rôle dans l\'opération, les quantités, le but recherché.';
    default:
      return '';
  }
}

export default function ContentUploader({ option, error, onSubmit, onBack }: Props) {
  const { user, previewPlan } = useAuth();
  // Mirror the gating logic used by AnalysisTypeSelector: only show admin-only
  // tooling to super-admins NOT currently previewing another plan.
  const isSuperAdmin = user?.isSuperAdmin === true && previewPlan === null;
  const showTrademarkPanel = isSuperAdmin && option.key === 'brand_name';

  const [textContent, setTextContent] = useState('');
  const [context, setContext] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [eventAudience, setEventAudience] = useState<EventAudience>('undecided');
  const [eventSupports, setEventSupports] = useState<EventSupport[]>([]);
  const [eventSupportDetails, setEventSupportDetails] = useState<Partial<Record<EventSupport, string>>>({});
  const [eventName, setEventName] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [fileWarning, setFileWarning] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_BYTES = 5 * 1024 * 1024;

  const isEvent = option.key === 'event_activation';
  const isText = option.inputType === 'text' || option.inputType === 'text+image';
  const isImage = option.inputType === 'image';
  const hasOptionalImages = option.inputType === 'text+image';
  // Event: accept either a description OR images (or both). Not strict on text.
  const canSubmit = isEvent
    ? textContent.trim().length > 0 || images.length > 0
    : isText ? textContent.trim().length > 0 : images.length > 0;

  const toggleSupport = (support: EventSupport) => {
    setEventSupports((prev) =>
      prev.includes(support) ? prev.filter((s) => s !== support) : [...prev, support],
    );
    // Clear the detail text when the user unchecks a support — avoids
    // sending stale details the user edited then deselected.
    setEventSupportDetails((prev) => {
      if (!prev[support]) return prev;
      const next = { ...prev };
      delete next[support];
      return next;
    });
  };

  const setSupportDetail = (support: EventSupport, value: string) => {
    setEventSupportDetails((prev) => ({ ...prev, [support]: value }));
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    const validType = arr.filter((f) =>
      ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(f.type),
    );
    const accepted = validType.filter((f) => f.size <= MAX_FILE_BYTES);
    const rejectedType = arr.length - validType.length;
    const rejectedSize = validType.length - accepted.length;

    const warnings: string[] = [];
    if (rejectedType > 0) {
      warnings.push(`${rejectedType} fichier(s) ignoré(s) : format non supporté (JPG, PNG, WebP uniquement).`);
    }
    if (rejectedSize > 0) {
      warnings.push(`${rejectedSize} fichier(s) ignoré(s) : poids supérieur à 5 Mo.`);
    }
    setFileWarning(warnings.length ? warnings.join(' ') : null);
    setImages((prev) => [...prev, ...accepted]);
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    // Keep only details for still-checked supports, trim & drop empties.
    const cleanedDetails: Partial<Record<EventSupport, string>> = {};
    for (const s of eventSupports) {
      const val = eventSupportDetails[s]?.trim();
      if (val) cleanedDetails[s] = val;
    }
    const eventMeta: EventActivationMeta | undefined = isEvent
      ? {
          audience: eventAudience,
          supports: eventSupports,
          supportDetails: Object.keys(cleanedDetails).length ? cleanedDetails : undefined,
          eventName: eventName.trim() || undefined,
          eventLocation: eventLocation.trim() || undefined,
          eventDate: eventDate.trim() || undefined,
          operationBrief: textContent.trim() || undefined,
        }
      : undefined;
    onSubmit({
      textContent: isText || isEvent ? textContent : undefined,
      context: context.trim() || undefined,
      images: images.length > 0 ? images : undefined,
      event: eventMeta,
    });
  };

  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      {/* Back + Title — en mode pastel, bandeau à l'aplat de la typologie
          (`p-hero`) : retour en cercle pointillé, icône seule dans une
          pastille blanche, titre encre. Sans effet dans les autres thèmes. */}
      <div className="p-hero flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          aria-label="Retour"
          className="p-back p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors text-slate-500 shader:text-white/60"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <div className="p-hero-pill hidden pastel:grid" aria-hidden="true">
          <AnalysisIcon type={option.key} className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-xl text-slate-900 dark:text-white pastel:text-(--p-ink) flex items-center gap-2">
            <AnalysisIcon type={option.key} className="w-5 h-5 text-slate-400 dark:text-white/50 pastel:hidden" />
            {option.label}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/50 pastel:text-(--p-ink)/70">{option.description}</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 shader:bg-red-900/20 shader:backdrop-blur-sm border border-red-200 dark:border-red-800 shader:border-red-500/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Text Input */}
      {(isText || isEvent) && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-2">
            {option.key === 'brand_name'
              ? 'Nom de marque souhaité'
              : option.key === 'slogan'
              ? 'Slogan ou accroche'
              : option.key === 'general_question'
              ? 'Votre question'
              : isEvent
              ? <>Brief de l'opération <span className="text-slate-400 shader:text-white/40 font-normal">(optionnel si vous uploadez des visuels)</span></>
              : 'Décrivez votre concept'}
          </label>
          <div className="glow-input">
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder={
                option.key === 'brand_name'
                  ? 'Ex: "Brasserie du Vieux Moulin" • "Korvel" • "Distillerie des Trois Sources"'
                  : option.key === 'slogan'
                  ? 'Ex: "Le terroir bourguignon dans votre verre" • "Brassée en Bretagne depuis 1985" • "L\'art de l\'assemblage, génération après génération" • "Brassée à l\'eau de source"'
                  : option.key === 'general_question'
                  ? 'Ex: "Ai-je le droit de mentionner des accords mets-vins sur mon site internet ?"'
                  : isEvent
                  ? 'Ex: "Portes ouvertes du domaine samedi, stand dans la cour, dégustation de 3 cuvées, distribution de tire-bouchons marqués, affichettes à l\'entrée, 2 hôtesses en tenue aux couleurs du domaine."'
                  : 'Décrivez votre concept créatif en détail...'
              }
              rows={option.key === 'concept' || option.key === 'general_question' || isEvent ? 6 : 3}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-3 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow resize-y min-h-[3rem]"
              autoFocus
            />
          </div>
        </div>
      )}

      {/* Trademark availability — admin-only pre-screen for brand_name */}
      {showTrademarkPanel && (
        <TrademarkAvailabilitySection initialBrandName={textContent} />
      )}

      {/* Image Upload (required for image types, optional for text+image) */}
      {(isImage || hasOptionalImages) && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-2">
            {option.key === 'label'
              ? 'Étiquette(s) de bouteille'
              : option.key === 'concept'
              ? <>Images d'inspiration <span className="text-slate-400 shader:text-white/40 font-normal">(optionnel)</span></>
              : isEvent
              ? <>Visuels de l'opération <span className="text-slate-400 shader:text-white/40 font-normal">(goodies, stand, PLV, affichettes — optionnel)</span></>
              : 'Visuels de campagne'}
          </label>

          {/* Drop Zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-violet-500'); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('border-violet-500'); }}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-violet-500'); handleFiles(e.dataTransfer.files); }}
            className="border-2 border-dashed border-slate-300 dark:border-slate-600 shader:border-white/20 pastel:border-(--tint-strong)/45 rounded-xl pastel:rounded-3xl p-8 text-center cursor-pointer hover:border-violet-400 dark:hover:border-violet-500 shader:hover:border-violet-400/40 pastel:hover:border-(--p-ink) shader:bg-white/5 pastel:bg-(--tint-soft)/70 pastel:hover:bg-(--tint-soft) shader:backdrop-blur-sm transition-colors"
          >
            <svg className="w-10 h-10 mx-auto mb-3 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Cliquez ou glissez-d&eacute;posez vos images ici
            </p>
            <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP — 5 Mo max par fichier</p>
            {hasOptionalImages && (
              <p className="text-xs text-violet-500/70 dark:text-violet-400/60 mt-2">Maquettes, prototypes, moodboards... pour enrichir l'analyse visuelle de votre concept</p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
            />
          </div>

          {/* File warning (size / type rejected client-side) */}
          {fileWarning && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              {fileWarning}
            </p>
          )}

          {/* Preview */}
          {images.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {images.map((img, i) => (
                <div key={i} className="relative group">
                  <div className="w-20 h-20 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
                    {img.type.startsWith('image/') ? (
                      <img
                        src={URL.createObjectURL(img)}
                        alt={img.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-xs text-slate-500">PDF</div>
                    )}
                  </div>
                  <button
                    onClick={() => removeImage(i)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Event Activation — nom/lieu/date → audience → supports (avec détail par support) */}
      {isEvent && (
        <>
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="glow-input">
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 shader:text-white/60 mb-1">Nom de l'opération</label>
              <input
                type="text"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="Ex: Portes ouvertes mai 2026"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>
            <div className="glow-input">
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 shader:text-white/60 mb-1">Lieu</label>
              <input
                type="text"
                value={eventLocation}
                onChange={(e) => setEventLocation(e.target.value)}
                placeholder="Ex: Vinexpo, caveau, bar partenaire"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>
            <div className="glow-input">
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 shader:text-white/60 mb-1">Date</label>
              <input
                type="text"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                placeholder="Ex: 15 mai 2026"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-2">
              Audience de l'opération
            </label>
            <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/50 mb-3">
              Le régime Loi Évin diffère selon que l'opération vise le circuit pro (goodies autorisés) ou le grand public (goodies interdits, L3323-6).
            </p>
            <div className="space-y-2">
              {EVENT_AUDIENCE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    eventAudience === opt.value
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 shader:bg-violet-500/10'
                      : 'border-slate-200 dark:border-slate-700 shader:border-white/10 hover:border-slate-300 dark:hover:border-slate-600 shader:hover:border-white/20'
                  }`}
                >
                  <input
                    type="radio"
                    name="eventAudience"
                    value={opt.value}
                    checked={eventAudience === opt.value}
                    onChange={() => setEventAudience(opt.value)}
                    className="mt-0.5 accent-violet-600"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900 dark:text-white shader:text-white">
                      {opt.label}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/50 mt-0.5">
                      {opt.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-2">
              Supports présents dans l'opération
            </label>
            <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/50 mb-3">
              Cochez chaque support concerné, puis précisez pourquoi, comment, combien, dans quel but. Plus vos explications sont précises, plus le verdict des juges sera affiné.
            </p>
            <div className="space-y-2">
              {EVENT_SUPPORT_OPTIONS.map((opt) => {
                const checked = eventSupports.includes(opt.value);
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg border transition-colors ${
                      checked
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 shader:bg-violet-500/10'
                        : 'border-slate-200 dark:border-slate-700 shader:border-white/10 hover:border-slate-300 dark:hover:border-slate-600 shader:hover:border-white/20'
                    }`}
                  >
                    <label className="flex items-start gap-3 p-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSupport(opt.value)}
                        className="mt-0.5 accent-violet-600"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900 dark:text-white shader:text-white">
                          {opt.label}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/50 mt-0.5">
                          {opt.description}
                        </div>
                      </div>
                    </label>
                    {checked && (
                      <div className="px-3 pb-3 pl-10 animate-fade-in">
                        <textarea
                          value={eventSupportDetails[opt.value] ?? ''}
                          onChange={(e) => setSupportDetail(opt.value, e.target.value)}
                          placeholder={eventSupportPlaceholder(opt.value)}
                          rows={3}
                          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow resize-y min-h-[3rem]"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Context (optional, always shown) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-2">
          Contexte additionnel <span className="text-slate-400 shader:text-white/40 font-normal">(optionnel)</span>
        </label>
        <div className="glow-input">
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Ex: marque de bière artisanale, cible 25-45 ans, distribution en grande surface..."
            rows={2}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow resize-none"
          />
        </div>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="font-display w-full py-3.5 rounded-xl pastel:rounded-full text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 shader:from-white shader:to-white shader:text-slate-900 shader:hover:from-white/90 shader:hover:to-white/90 pastel:bg-none pastel:bg-violet-500 pastel:hover:bg-violet-600 pastel:hover:scale-[1.01] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all shadow-lg shadow-violet-200 dark:shadow-violet-900/30 shader:shadow-white/10 pastel:shadow-none"
      >
        {option.key === 'general_question' ? 'Poser ma question' : 'Lancer l\'analyse Loi \u00C9vin'}
      </button>
    </div>
  );
}
